/**
 * ProfileModal - User profile editing modal
 * Loads and saves user data from Firestore 'users' collection
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { auth } from '../../firebase';
import { loadUserProfile, saveUserProfile, deleteUserProfile } from '../../services/userService';
import './ProfileModal.css';

const AUTH_API_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:3001'
  : 'https://auth.findplayers.fr';

const ProfileModal = ({ isOpen, onClose }) => {
  const { currentUser, signOut } = useAuth();
  const { showSuccess } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    birthday: '',
    phoneNumber: ''
  });

  useEffect(() => {
    if (isOpen && currentUser) {
      setLoading(true);
      loadUserProfile(currentUser.uid)
        .then((data) => {
          if (data) {
            setFormData({
              lastName: data.lastName || '',
              firstName: data.firstName || '',
              birthday: data.birthday || '',
              phoneNumber: data.phoneNumber?.phoneNumber || data.phoneNumber || ''
            });
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [isOpen, currentUser]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDelete = async () => {
    if (!currentUser) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteUserProfile(currentUser.uid);

      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${AUTH_API_URL}/api/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erreur serveur');
      }

      onClose();
      await signOut();
    } catch (error) {
      setDeleteError(error.message || 'Erreur lors de la suppression du compte.');
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;
    setSaving(true);
    try {
      await saveUserProfile(currentUser.uid, {
        lastName: formData.lastName,
        firstName: formData.firstName,
        birthday: formData.birthday,
        phoneNumber: formData.phoneNumber
      });
      showSuccess('Profil mis à jour !');
      onClose();
    } catch {
      showSuccess('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      ></div>

      <div
        className="modal fade show d-block profile-modal"
        tabIndex="-1"
        role="dialog"
        style={{ zIndex: 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-person-circle me-2"></i>
                Mon profil
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body">
              {loading ? (
                <div className="profile-loading">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Chargement...
                </div>
              ) : (
                <>
                  {/* Email (read-only) */}
                  <div className="profile-field">
                    <label>Email</label>
                    <div className="profile-email">{currentUser?.email}</div>
                  </div>

                  <div className="profile-field">
                    <label htmlFor="profile-lastName">Nom</label>
                    <input
                      type="text"
                      id="profile-lastName"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      placeholder="Votre nom"
                    />
                  </div>

                  <div className="profile-field">
                    <label htmlFor="profile-firstName">Prénom</label>
                    <input
                      type="text"
                      id="profile-firstName"
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      placeholder="Votre prénom"
                    />
                  </div>

                  <div className="profile-field">
                    <label htmlFor="profile-birthday">Date de naissance</label>
                    <input
                      type="date"
                      id="profile-birthday"
                      value={formData.birthday}
                      onChange={(e) => handleChange('birthday', e.target.value)}
                    />
                  </div>

                  <div className="profile-field">
                    <label htmlFor="profile-phone">Téléphone</label>
                    <input
                      type="tel"
                      id="profile-phone"
                      value={formData.phoneNumber}
                      onChange={(e) => handleChange('phoneNumber', e.target.value)}
                      placeholder="Votre numéro"
                    />
                  </div>

                  <hr className="profile-separator" />

                  {deleteError && (
                    <div className="profile-error">{deleteError}</div>
                  )}

                  {!confirmDelete ? (
                    <button
                      type="button"
                      className="profile-delete-btn"
                      onClick={() => setConfirmDelete(true)}
                      disabled={saving}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Supprimer mon compte
                    </button>
                  ) : (
                    <div className="profile-delete-confirm">
                      <p className="profile-delete-warning">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Cette action est irréversible. Votre compte et vos données seront définitivement supprimés.
                      </p>
                      <div className="profile-delete-actions">
                        <button
                          type="button"
                          className="profile-delete-cancel"
                          onClick={() => { setConfirmDelete(false); setDeleteError(''); }}
                          disabled={deleting}
                        >
                          Annuler
                        </button>
                        <button
                          type="button"
                          className="profile-delete-execute"
                          onClick={handleDelete}
                          disabled={deleting}
                        >
                          {deleting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1"></span>
                              Suppression...
                            </>
                          ) : (
                            'Confirmer la suppression'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="profile-btn-cancel"
                onClick={onClose}
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="button"
                className="profile-btn-save"
                onClick={handleSave}
                disabled={loading || saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1"></i>
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileModal;
